/**
 * Réglages de la synchro Chess.com, en localStorage (propres à ce
 * navigateur, comme le reste du stockage de Magnus). Lecture/écriture
 * protégées : en navigation privée ou si le stockage est bloqué, on retombe
 * silencieusement sur les valeurs par défaut plutôt que de planter.
 */

export interface ChessComSettings {
  username: string;
  /** Cadences synchronisées automatiquement (valeurs `time_class` de l'API Chess.com). */
  timeClasses: string[];
  autoAnalyze: boolean;
  lastSyncAt?: string; // ISO
  lastSyncEndTime?: number; // epoch secondes de la partie la plus récente déjà synchronisée
}

const KEY = 'magnus:chesscom';
const LEGACY_USERNAME_KEY = 'magnus:chesscom-username';

export const DEFAULT_TIME_CLASSES = ['rapid', 'blitz'];

function defaults(username = ''): ChessComSettings {
  return { username, timeClasses: DEFAULT_TIME_CLASSES, autoAnalyze: true };
}

export function loadChessComSettings(): ChessComSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ChessComSettings>;
      return {
        username: typeof parsed.username === 'string' ? parsed.username : '',
        timeClasses:
          Array.isArray(parsed.timeClasses) && parsed.timeClasses.length > 0
            ? parsed.timeClasses.filter((t): t is string => typeof t === 'string')
            : DEFAULT_TIME_CLASSES,
        autoAnalyze: parsed.autoAnalyze !== false,
        lastSyncAt: typeof parsed.lastSyncAt === 'string' ? parsed.lastSyncAt : undefined,
        lastSyncEndTime: typeof parsed.lastSyncEndTime === 'number' ? parsed.lastSyncEndTime : undefined,
      };
    }
    // Migration depuis l'ancienne clé (pseudo seul, avant l'introduction des réglages).
    const legacyUsername = localStorage.getItem(LEGACY_USERNAME_KEY);
    if (legacyUsername) {
      const migrated = defaults(legacyUsername);
      saveChessComSettings(migrated);
      localStorage.removeItem(LEGACY_USERNAME_KEY);
      return migrated;
    }
  } catch {
    // localStorage indisponible : réglages par défaut, non persistés pour cette session.
  }
  return defaults();
}

export function saveChessComSettings(settings: ChessComSettings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    // tant pis, les réglages ne seront pas mémorisés d'une session à l'autre
  }
}
